<template>
  <b-card>
    <b-card-body>
      <div class="overflow-x-auto">
        <div
          class="table_Table"
          v-for="(usuariosGroup, index) in usuarios"
          :key="index + 'por_pais'"
        >
          <div class="title_Table">
            {{ usuariosGroup.title }}
            <b-icon
              class="float-right icon mr-2"
              icon="chevron-down"
              aria-label="Collapse Toggle"
              :flip-v="usuariosGroup.collapsed"
              @click="usuariosGroup.collapsed = !usuariosGroup.collapsed"
            />
          </div>
          <b-collapse v-model="usuariosGroup.collapsed" class="overflow-x-auto">
            <div class="table-responsive">
              <b-table
                ref="tableUsuarios"
                :fields="visibleFields"
                :items="usuariosGroup.items"
                :sort-by.sync="usuariosGroup.orderBy"
                :sort-desc.sync="usuariosGroup.sortDesc"
                :empty-text="$t('label.noData')"
                no-border-collapse
                show-empty
                sort-icon-left
                @sort-changed="onSortingChange($event, usuariosGroup)"
                table-class="overflow-x-auto"
                :responsive="false"
                borderless
                :no-local-sorting="true"
              >
                <div class="text-center loader my-2" slot="table-busy">
                  <b-spinner class="align-middle" />
                </div>

                <template #head(actions-right)>
                  <select-columns
                    :fields.sync="fields"
                    :pagination="pagination"
                    :cod-table="codTable"
                  />
                </template>

                <template #cell(actions-left)="row">
                  <div class="table-button-container">
                    <div class="divActionCell_Table" @click="clickAcciones('modificar', row.item)">
                      <img
                        class="editar-icon ml-3"
                        src="@/assets/icons/editar-icon.svg"
                        alt="Modificar"
                      />
                    </div>
                  </div>
                </template>

                <template #cell(desNombre)="row">
                  <span>
                    {{ row.item.desNombre + " " + row.item.desApellidos }}
                  </span>
                </template>
              </b-table>
            </div>
            <div class="mt-3" v-if="usuariosGroup.totalElements > usuariosGroup.pagination.size">
              <pagination
                :current-page="usuariosGroup.pagination.page"
                :page-size="usuariosGroup.pagination.size"
                :total-elements="usuariosGroup.totalElements"
                @change="onChangeCurrentPage($event, usuariosGroup)"
              ></pagination>
            </div>
          </b-collapse>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { IConfColumnaPais, IField, ISoConfColumnasUpdateDTO } from "@/entities";
import { Action } from "vuex-class";
import Pagination from "@/components/common/Pagination.vue";
import VueFilters from "@/vue-filters";

import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";
import { EDirection, IColumnaResponse, IPaged, IUsuarioTableContent } from "@/services";
import IconAdministracion from "@/components/common/icon/IconAdministracion.vue";

@Component({
  components: {
    IconAdministracion,
    SelectColumns,
    Pagination,
  },
  mixins: [VueFilters],
})
export default class UsuariosTable extends Vue {
  @Prop({ required: true }) columnsConfig!: IColumnaResponse;
  @Prop({ required: true }) usuarios!: IUsuarioTableContent[];
  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;

  codTable = "usuarios";
  sortDesc = this.columnsConfig?.codTipoOrden !== "ASC";
  totalElements = 0;

  pagination: IPaged = {
    direction: this.sortDesc ? EDirection.DESC : EDirection.ASC,
    orderBy: this.columnsConfig?.codOrden ?? "id",
    page: 1,
    size: 10,
  };

  get fields(): IField[] {
    const visibleKeys = this.columnsConfig?.codColumna?.split(",").filter((s) => s !== "") || [];
    let columnas: IField[] = [
      {
        key: "actions-left",
        label: this.$t("label.acciones"),
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first actions",
        visible: true,
        class: "table-cell",
        aux: true,
        code: "",
      },
    ];
    this.columnsConfig.columnas?.forEach((column: IConfColumnaPais) => {
      columnas.push({
        key: column.keyFront,
        label: this.$t("label." + column.codColumna),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thStyle: column.widthFront ? { width: column.widthFront } : {},
        visible: this.isVisible(visibleKeys, column.codColumna),
        class: "table-cell",
        aux: false,
        code: column.codColumna,
        formatter: (value: any): string => {
          if (column.formatType == "Date") {
            return this.filterDate(value);
          } else if (column.formatType == "Number") {
            return this.filterNumber(value, 2);
          } else if (column.formatType == "Boolean") {
            return value ? this.$t("label.si") : this.$t("label.no");
          }
          return value;
        },
      });
    });
    columnas.push({
      key: "actions-right",
      label: "",
      sortable: false,
      thClass: "table-header table-header-add-columnas pb-0",
      tdClass: "table-cell-middle actions",
      visible: true,
      class: "table-cell",
      aux: true,
      code: "",
    });
    return columnas;
  }

  set fields(fields: IField[]) {
    this.columnsConfig.codColumna =
      fields
        .filter((item) => item.visible && !item.aux)
        .map((item) => item.code)
        .join(",") || "";
  }

  get visibleFields(): IField[] {
    return this.fields.filter((item) => item.visible);
  }

  clickAcciones(accion: string, row: unknown): void {
    if (accion === "modificar") {
      this.$emit("edit", row);
    }
  }

  onSortingChange(
    obj: { sortBy: string; sortDesc: boolean },
    usuariosGroup: IUsuarioTableContent
  ): void {
    this.$nextTick(async () => {
      const sortDesc = usuariosGroup ? usuariosGroup.sortDesc : true;
      const orderBy = usuariosGroup ? usuariosGroup.orderBy : "id";
      const pagination: IPaged = {
        direction: sortDesc ? EDirection.DESC : EDirection.ASC,
        orderBy,
        page: usuariosGroup.pagination.page,
        size: usuariosGroup.pagination.size,
      };
      await this.saveColumnsConfig();
      this.$emit("filter", {
        action: "sortingChange",
        dataFilters: {
          pagination,
          configurado: usuariosGroup.configurado,
        },
      });
    });
  }

  onChangeCurrentPage(currentPage: number, usuariosGroup: IUsuarioTableContent): void {
    const sortDesc = usuariosGroup ? usuariosGroup.sortDesc : true;
    const orderBy = usuariosGroup ? usuariosGroup.orderBy : "id";
    const pagination: IPaged = {
      direction: sortDesc ? EDirection.DESC : EDirection.ASC,
      orderBy,
      page: currentPage,
      size: usuariosGroup.pagination.size,
    };

    this.$emit("change", {
      action: "changePagination",
      dataFilters: {
        configurado: usuariosGroup.configurado,
        pagination,
      },
    });
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.columnsConfig.codColumna || "",
      codTabla: this.columnsConfig.codTabla,
    };
    if (this.pagination) {
      params.codTipoOrden = this.pagination.direction;
      params.numPagElements = this.pagination.size;
      params.codOrden = this.pagination.orderBy;
    }
    await this.saveColumna(params);
  }

  isVisible(visibleKeys: string[], column: string): boolean {
    return !visibleKeys.length || !!visibleKeys.find((key) => key === column);
  }
}
</script>
